<script setup lang="ts">
    import DraggableResizableVue from 'draggable-resizable-vue3';
    import { useDisplay } from 'vuetify';
    import DialogHeader from '@/components/DialogHeader.vue';
    import { useWindowResize } from '@/composables/useWindowResize';
    import { WINDOW_TOP_OFFSET } from '@/constants';

    const { mobile } = useDisplay();
    const { windowWidth, windowHeight } = useWindowResize();

    const {
        windowId,
        initialX,
        initialY,
        initialW,
        initialH,
        zIndex,
        active,
        draggable = true,
        resizable = true,
        dragHandle = '.drag',
        isFullscreen = false,
        isMinimized = false,
        headerTitle,
        headerIcon = null,
        showFullscreenButton = false,
        showFiltersButton = false,
        showJsonToggleButton = false,
        onFullscreenToggle,
    } = defineProps<{
        windowId: string;
        initialX: number;
        initialY: number;
        initialW: number;
        initialH: number;
        zIndex: number;
        active: boolean;
        draggable?: boolean;
        resizable?: boolean;
        dragHandle?: string;
        isFullscreen?: boolean;
        isMinimized?: boolean;
        headerTitle?: string;
        headerIcon?: string | null;
        showFullscreenButton?: boolean;
        showFiltersButton?: boolean;
        showJsonToggleButton?: boolean;
        onFullscreenToggle?: () => void;
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
        (e: 'minimize'): void;
        (e: 'update:position', x: number, y: number): void;
        (e: 'update:size', w: number, h: number): void;
        (e: 'activate'): void;
    }>();

    const x = ref(initialX);
    const y = ref(initialY);
    const w = ref(initialW);
    const h = ref(initialH);

    const computedX = computed(() => {
        if (mobile.value || isFullscreen) return 0;
        return x.value;
    });
    const computedY = computed(() => {
        if (mobile.value || isFullscreen) return WINDOW_TOP_OFFSET;
        return y.value;
    });
    const computedW = computed(() => {
        if (mobile.value || isFullscreen) return windowWidth.value;
        return w.value;
    });
    const computedH = computed(() => {
        if (mobile.value || isFullscreen) return windowHeight.value - WINDOW_TOP_OFFSET;
        return h.value;
    });
    const computedMinWidth = computed(() => (mobile.value ? windowWidth.value : 400));
    const computedMinHeight = computed(() => (mobile.value ? windowHeight.value - WINDOW_TOP_OFFSET : 300));

    watch(
        () => initialX,
        (newX) => {
            if (Math.abs(x.value - newX) > 5) {
                x.value = newX;
            }
        }
    );

    watch(
        () => initialY,
        (newY) => {
            if (Math.abs(y.value - newY) > 5) {
                y.value = newY;
            }
        }
    );

    watch(
        () => initialW,
        (newW) => {
            if (Math.abs(w.value - newW) > 5) {
                w.value = newW;
            }
        }
    );

    watch(
        () => initialH,
        (newH) => {
            if (Math.abs(h.value - newH) > 5) {
                h.value = newH;
            }
        }
    );

    const handleActivated = () => {
        emit('activate');
    };

    const handleXUpdate = (newX: number) => {
        x.value = newX;
        emit('update:position', newX, y.value);
    };

    const handleYUpdate = (newY: number) => {
        y.value = newY;
        emit('update:position', x.value, newY);
    };

    const handleWUpdate = (newW: number) => {
        w.value = newW;
        emit('update:size', newW, h.value);
    };

    const handleHUpdate = (newH: number) => {
        h.value = newH;
        emit('update:size', w.value, newH);
    };

    const handleClose = () => {
        emit('close');
    };

    const handleMinimize = () => {
        emit('minimize');
    };

    const handleFullscreenToggle = () => {
        if (onFullscreenToggle) {
            onFullscreenToggle();
        }
    };
    const handleDragStop = (left: number, top: number) => {
        let newY = top;

        if (newY < WINDOW_TOP_OFFSET) {
            newY = WINDOW_TOP_OFFSET;
        }

        y.value = newY;
        x.value = left;

        emit('update:position', x.value, y.value);
    };
</script>

<template>
    <DraggableResizableVue
        :x="computedX"
        :y="computedY"
        :w="computedW"
        :h="computedH"
        :min-width="computedMinWidth"
        :min-height="computedMinHeight"
        :draggable="!mobile && !isFullscreen && draggable"
        :resizable="!mobile && !isFullscreen && resizable"
        :parent="false"
        :active="active && !mobile"
        :drag-handle="dragHandle ?? '.drag'"
        drag-cancel=".v-btn, button, .v-icon"
        :style="{ zIndex, display: isMinimized ? 'none' : undefined }"
        class="draggable-window"
        handles-type="borders"
        :data-window-id="windowId"
        @update:x="handleXUpdate"
        @update:y="handleYUpdate"
        @update:w="handleWUpdate"
        @update:h="handleHUpdate"
        @activated="handleActivated"
        @click="handleActivated"
        @dragstop="handleDragStop"
    >
        <v-card class="h-100 d-flex flex-column" elevation="8">
            <DialogHeader
                v-if="headerTitle"
                :title="headerTitle"
                :icon="headerIcon"
                :window-id="windowId"
                :show-filters-button="showFiltersButton"
                :show-json-toggle-button="showJsonToggleButton"
                :show-fullscreen-button="showFullscreenButton && !mobile"
                :is-fullscreen="isFullscreen"
                :on-fullscreen-toggle="handleFullscreenToggle"
                show-minimize-button
                @close="handleClose"
                @minimize="handleMinimize"
                class="drag"
            />
            <slot />
        </v-card>
    </DraggableResizableVue>
</template>

<style lang="scss" scoped>
    .draggable-window {
        position: fixed;
        border-radius: 3px;
        border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }

    .drag {
        cursor: move;
    }
</style>
